import React, { useEffect, useState } from 'react';
import {
  batch, shallowEqual, useDispatch, useSelector
} from 'react-redux';
import { useSnackbar } from 'notistack';

import Text from '../../Text/Text';
import StepperNavigationBtn from './StepperNavigationBtn';
import Button from '../../Button/Button';
import postEasyPostShipment from '../../../thunks/shipment/easyPost/postEasyPostShipment';
import clearCreateNewOrder, { setCreateNewOrderData } from '../../../thunks/coilovers/orders/createNewOrder';
import Input from '../../Input/Input';
import CustomSelect from '../../Select/CustomSelect';
import postEasyPostShipmentClearError from '../../../actions/shipment/easyPost/postEasyPostShipment';
import postEasyPostBuyShipment from '../../../thunks/shipment/easyPost/postEasyPostBuyShipment';
import postCoOrder from '../../../thunks/coilovers/orders/postCoOrder';
import { IconLoading } from '../../../assets/icon';
import prepareOrderData from '../../../helpers/prepareOrderData';
import ConfirmModal from '../../ConfirmModal/ConfirmModal';
import { clearPostCoOrder } from '../../../actions/coilovers/orders/postCoOrder';
import postEasyShipShipmentClearError from '../../../actions/shipment/easyShip/postEasyShipShipment';
import postEasyShipBuyShipment from '../../../thunks/shipment/easyShip/postEasyShipBuyShipment';
import Alert from '../../Alert/Alert';
import AlertType from '../../../common/alertType';
import postEasyShipRates from '../../../thunks/shipment/easyShip/postEasyShipRates';
import Checkbox from '../../Checkbox/Checkbox';
import postAnnShipShipment from '../../../thunks/shipment/annShip/postAnnShipShipment';
import annShipServiceTypes from '../../../constants/annShipServiceTypes';
import { postAnnShipShipmentClearError } from '../../../reducers/shipment/annShip/postAnnShipShipment';
import postAnnShipBuyShipment from '../../../thunks/shipment/annShip/postAnnShipBuyShipment';

const OrderShippingForm = () => {
  const dispatch = useDispatch();
  const [selectProvider, setSelectProvider] = useState();
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [isInsurance, setIsInsurance] = useState(false);
  const {
    createNewOrderData,
    createNewOrderData: {
      weight,
      height,
      length,
      width,
      declared,
      order_assembly_invoice: invoice,
      order_label_file: label,
      order_label_file2: label2,
      address,
      address2,
      firstName,
      lastName,
      country,
      state: clientState,
      city,
      zip,
      phone,
      email,
      order_shipment_tracking_number: trackingCode,
      totalPrice,
      totalPriceOriginal,
      referenceNo,
    },
    easyPostShipment: {
      loading: easyPostShipmentLoading,
      error: easyPostShipmentError,
      data: {
        id: easyPostShipmentId,
        rates: easyPostShipmentRates = [],
        messages: easyPostShipmentMessages = [],
      } = {},
    } = {},
    annShipShipment: {
      error: postAnnShipShipmentError,
      loading: postAnnShipRatesLoading,
      data: {
        rates: annShipRates = [],
      } = {}
    } = {},
    postEasyShipRates: {
      loading: postEasyShipRatesLoading,
      error: postEasyShipRatesError,
      data: {
        errors: easyShipRatesErrors,
        rates: easyShipShipmentRates = [],
      } = {}
    } = {},
    getNextIncrementValue: {
      co_order_id: coOrderId
    } = {},
    postEasyPostBuyShipment: {
      loading: postEasyPostBuyShipmentLoading,
      data: {
        s3_label_url: easyPostLabelUrl,
      } = {}
    },
    postEasyShipBuyShipment: {
      loading: postEasyShipBuyShipmentLoading,
      data: {
        s3_label_url: easyShipLabelUrl,
      } = {}
    } = {},
    annShipBuyShipment: {
      loading: postAnnShipBuyShipmentLoading,
      data: {
        s3_label_url: annShipLabelUrl,
      } = {}
    } = {},
    postCoOrder: {
      loading: postCoOrderLoading,
      error: postCoOrderError,
      data: {
        order_id: orderId
      } = {}
    },
  } = useSelector((state) => ({
    createNewOrderData: state.createNewOrder?.data,
    easyPostShipment: state.postEasyPostShipment,
    getNextIncrementValue: state.getNextIncrementValue.data,
    postEasyPostBuyShipment: state.postEasyPostBuyShipment,
    postEasyShipBuyShipment: state.postEasyShipBuyShipment,
    postCoOrder: state.postCoOrder,
    postEasyShipRates: state.postEasyShipRates,
    annShipShipment: state.postAnnShipShipment,
    annShipBuyShipment: state.postAnnShipBuyShipment,
  }), shallowEqual);

  const {
    enqueueSnackbar,
  } = useSnackbar();

  const orderData = {
    to_address: {
      name: `${firstName} ${lastName} (${coOrderId})`,
      street1: address,
      street2: address2,
      city,
      state: clientState,
      zip,
      country,
      phone,
      email
    },
    parcel: {
      length,
      width,
      height,
      weight: weight * 16, // convert to oz
      price: parseFloat(totalPrice)
    },
    po_number: referenceNo || `C${coOrderId}`,
    insurance: isInsurance ? parseFloat(declared) : 0
  };

  const handleGetShipment = () => {
    setSelectProvider(null);

    batch(() => {
      dispatch(postEasyPostShipment(orderData));
      dispatch(postEasyShipRates(orderData));
      dispatch(postAnnShipShipment(orderData));
    });
  };

  const handleCreateShipment = () => {
    if (selectProvider?.billing_type === 'easypost') {
      dispatch(postEasyPostBuyShipment({
        insurance: parseFloat(declared),
        rate_id: selectProvider.id,
        shipment_id: easyPostShipmentId
      }));
    }
    if (selectProvider?.billing_type === 'easyship') {
      dispatch(postEasyShipBuyShipment({
        ...orderData,
        rate_id: selectProvider?.courier_id
      }));
    }
    if (selectProvider?.billing_type === 'annship') {
      dispatch(postAnnShipBuyShipment({
        service_type: selectProvider?.servicetype,
        service_provider: selectProvider?.serviceprovider,
        ...orderData
      }));
    }
  };

  const onCreateOrder = () => {
    dispatch(postCoOrder({
      ...prepareOrderData(createNewOrderData),
      order_id: coOrderId,
      order_po_number: `C${coOrderId}`,
      order_shipment_tracking_number: trackingCode,
      order_shipping_service: selectProvider?.carrier,
    }));
  };

  const inputData = [
    [
      {
        id: 'weight',
        value: weight,
        placeholder: 'Weight',
        multiline: false,
        type: 'number',
        required: true,
        step: 0.1
      },
      {
        id: 'width',
        value: width,
        placeholder: 'Width',
        multiline: false,
        type: 'number',
        required: true,
        step: 0.1
      },
      {
        id: 'height',
        value: height,
        placeholder: 'Height',
        multiline: false,
        type: 'number',
        required: true,
        step: 0.1
      },
    ],
  ];

  useEffect(() => {
    if (easyPostShipmentError || postEasyShipRatesError || postAnnShipShipmentError) {
      enqueueSnackbar(easyPostShipmentError || postEasyShipRatesError || postAnnShipShipmentError, {
        variant: 'error',
        autoHideDuration: 5000
      });
      dispatch(postEasyPostShipmentClearError());
      dispatch(postEasyShipShipmentClearError());
      dispatch(postAnnShipShipmentClearError());
    }
    if (easyPostLabelUrl || easyShipLabelUrl || annShipLabelUrl) {
      enqueueSnackbar('Label created', {
        variant: 'success',
        autoHideDuration: 5000
      });
      dispatch(postEasyPostShipmentClearError());
      dispatch(postEasyShipShipmentClearError());
      dispatch(postAnnShipShipmentClearError());
    }
  }, [
    easyPostShipmentError, postEasyShipRatesError, postAnnShipShipmentError,
    easyPostLabelUrl, easyShipLabelUrl, annShipLabelUrl
  ]);

  useEffect(() => {
    if (postCoOrderError) {
      enqueueSnackbar(postCoOrderError, {
        variant: 'error',
        autoHideDuration: 5000
      });
      dispatch(clearPostCoOrder());
    }
  }, [dispatch, enqueueSnackbar, postCoOrderError]);

  useEffect(() => {
    if (orderId) {
      enqueueSnackbar('Order created', {
        variant: 'success',
        autoHideDuration: 5000
      });
      setConfirmVisible(false);
      batch(() => {
        dispatch(clearPostCoOrder());
        dispatch(clearCreateNewOrder());
      });
    }
  }, [orderId]);

  useEffect(() => {
    if (totalPriceOriginal && totalPriceOriginal >= 500) {
      setIsInsurance(true);
    }
  }, [totalPriceOriginal]);

  const prepareRates = () => {
    const easyPostRates = easyPostShipmentRates?.map((elem) => ({
      ...elem,
      billing_type: 'easypost',
      label: `[EasyPost]: ${elem.carrier} - ${elem.service} ($${elem.rate})`,
      value: elem.id
    }));
    const easyShip = easyShipShipmentRates?.map((elem) => ({
      ...elem,
      rate: elem?.shipment_charge_total,
      billing_type: 'easyship',
      label: `[EasyShip]: ${elem.courier_name} ($${elem.shipment_charge_total})`,
      value: elem.courier_id
    }));

    const annShip = annShipRates?.filter(Boolean)?.map((elem) => ({
      ...elem,
      rate: elem?.totalcharge,
      billing_type: 'annship',
      label: `[Annship]: 
      ${annShipServiceTypes[elem?.servicetype] || elem?.servicetype || '-'} ($${elem?.totalcharge || '-'})`,
    }));

    return [...easyPostRates, ...easyShip, ...annShip];
  };

  return (
    <div className="flex flex-col flex-1">
      <div className="pb-[50px]">
        <Text text="Shipping Details" textStyle="text-[20px] text-[#252F4A] mb-[6px]" />
      </div>
      <div>
        <div className="mb-[30px]">
          <div className="flex flex-row">
            <div className="flex1">
              <Text
                text="Invoice"
                textStyle="text-[#252F4A] text-[14px]"
                styleContainer="mb-[10px]"
              />
              <Input
                type="text"
                name="invoice"
                value={invoice}
                placeholder="Invoice"
                onChange={
                  ({ target: { value: val } }) => dispatch(setCreateNewOrderData({ order_assembly_invoice: val }))
                }
              />
            </div>
          </div>
          <div className="flex flex-row">
            <div className="flex1">
              <Text
                text="Label"
                textStyle="text-[#252F4A] text-[14px]"
                styleContainer="mb-[10px]"
              />
              <Input
                type="text"
                name="label"
                value={label}
                placeholder="Label"
                onChange={
                  ({ target: { value: val } }) => dispatch(setCreateNewOrderData({ order_label_file: val }))
                }
              />
            </div>
          </div>
          <div className="flex flex-row">
            <div className="flex1">
              <Text
                text="Label2"
                textStyle="text-[#252F4A] text-[14px]"
                styleContainer="mb-[10px]"
              />
              <Input
                type="text"
                name="label2"
                value={label2}
                placeholder="Label2"
                onChange={
                  ({ target: { value: val } }) => dispatch(setCreateNewOrderData({ order_label_file2: val }))
                }
              />
            </div>
          </div>
        </div>
        {inputData?.map((items, index) => (
          <div className="mb-[30px]" key={['section', index].join('_')}>
            <div className="flex flex-row gap-[20px]">
              {items?.map(({
                id,
                value,
                placeholder,
                multiline,
                type,
                required,
                step
              }) => (
                <div className="flex1" key={id}>
                  <Text
                    text={placeholder}
                    textStyle="text-[#252F4A] text-[14px]"
                    styleContainer="mb-[10px]"
                    required={required}
                  />
                  <Input
                    step={step}
                    type={type || 'text'}
                    name={id}
                    value={value}
                    placeholder={placeholder}
                    multiline={multiline}
                    rows={3}
                    onChange={({ target: { value: val } }) => dispatch(setCreateNewOrderData({ [id]: val }))}
                  />
                </div>
              ))}
            </div>
          </div>
        ))}
        <div className="mb-[30px]">
          <div className="flex flex-row items-center gap-[20px]">
            <div className="flex1">
              <Text
                text="Length"
                textStyle="text-[#252F4A] text-[14px]"
                styleContainer="mb-[10px]"
                required
              />
              <Input
                type="number"
                name="length"
                value={length}
                placeholder="Length"
                onChange={({ target: { value: val } }) => dispatch(setCreateNewOrderData({ length: val }))}
                step={0.1}
              />
            </div>
            <div className="flex1">
              <Text
                text="Declared customs value"
                textStyle="text-[#252F4A] text-[14px]"
                styleContainer="mb-[10px]"
              />
              <Input
                type="number"
                step={0.01}
                name="declared"
                value={declared}
                placeholder="Feclared customs value"
                onChange={({ target: { value: val } }) => dispatch(setCreateNewOrderData({ declared: val }))}
              />
            </div>
            <Button
              text="Get Quote"
              onClick={handleGetShipment}
              disabled={!weight || !height || !length || !width || easyPostShipmentLoading}
              containerStyle="p-[16px] mt-[13px]"
              tooltip={(!weight || !height || !length || !width) ? 'Please fill all fields first' : ''}
              tooltipId="get_quote_tooltip"
            />
          </div>
          <div className="flex flex-col gap-[20px]">
            {easyShipRatesErrors?.map((error) => (
              <Alert text={`EasyShip: ${error}`} type={AlertType.warning} />
            ))}
            {easyPostShipmentMessages?.map(({ message }) => (
              <Alert text={`EasyPost: ${message}`} type={AlertType.warning} />
            ))}
          </div>
        </div>
        <div className="mb-[30px]">
          <div className="flex flex-col">
            <Checkbox
              name="insurance"
              text="Include insurance?"
              styleContainer="d-justify-start"
              isActive={isInsurance}
              onChange={() => setIsInsurance(!isInsurance)}
            />
          </div>
        </div>
        <div className="mb-[30px]">
          <div className="flex1">
            <Text
              text="Providers"
              textStyle="text-[#252F4A] text-[14px]"
              styleContainer="mb-[10px]"
            />
            <CustomSelect
              placeholder={easyPostShipmentRates.length ? 'Select provider' : 'No providers available'}
              isDisabled={!easyPostShipmentRates.length}
              isLoading={easyPostShipmentLoading || postEasyShipRatesLoading || postAnnShipRatesLoading}
              options={prepareRates()
                .sort((a, b) => a?.rate - b?.rate)}
              value={[selectProvider]}
              isMulti={false}
              onChange={(data) => setSelectProvider(data)}
              containerStyle={{
                paddingTop: 13,
                paddingBottom: 13,
                paddingLeft: 26,
                paddingRight: 26,
                fontSize: 16
              }}
            />
          </div>
          <div className="flex1">
            <br />
            <br />
            <Button
              text="Create shippment"
              icon={(postEasyPostBuyShipmentLoading || postEasyShipBuyShipmentLoading || postAnnShipBuyShipmentLoading)
                ? <div className="spin"><IconLoading /></div> : undefined}
              onClick={handleCreateShipment}
              disabled={!selectProvider
                || easyPostShipmentLoading
                || postEasyPostBuyShipmentLoading
                || postEasyShipBuyShipmentLoading
                || postAnnShipBuyShipmentLoading
                || label?.length
                || label2?.length}
              tooltip={!selectProvider ? 'Please select provider first' : ''}
              tooltipId="create_shippment_tooltip"
            />
          </div>
        </div>
      </div>
      <StepperNavigationBtn
        next="shipping"
        prev="product"
        nextText="Submit"
        onSubmit={() => setConfirmVisible(true)}
        loading={postCoOrderLoading}
        disabledNext={
          !address
          || !firstName
          || !lastName
          || !country
          || !clientState || !city || !zip || !phone || postCoOrderLoading
        }
      />
      <ConfirmModal
        onConfirm={onCreateOrder}
        visible={confirmVisible}
        toggleVisible={() => setConfirmVisible(false)}
      />
    </div>
  );
};

export default OrderShippingForm;
